import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePageShortcutsRegistry } from "@/hooks/usePageShortcuts";

const isTypingTarget = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
};

type Shortcut = { keys: string[]; label: string };
type Section = { heading: string; items: Shortcut[] };

const GLOBAL_SECTIONS: Section[] = [
  {
    heading: "Navigation",
    items: [
      { keys: ["n"], label: "New excerpt" },
      { keys: ["/"], label: "Focus search (or open Search)" },
      { keys: ["g", "e"], label: "Go to My Excerpts" },
      { keys: ["g", "h"], label: "Go to Home (My Excerpts)" },
      { keys: ["g", "r"], label: "Go to Highlights" },
      { keys: ["g", "s"], label: "Go to Settings" },
    ],
  },
  {
    heading: "Help",
    items: [{ keys: ["?"], label: "Show this shortcuts dialog" }],
  },
];

const Kbd = ({ children }: { children: React.ReactNode }) => (
  <kbd className="inline-flex items-center justify-center rounded border border-border bg-muted px-2 py-0.5 text-xs font-mono text-foreground min-w-[1.75rem]">
    {children}
  </kbd>
);

interface GlobalShortcutsDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const GlobalShortcutsDialog = ({ open: openProp, onOpenChange }: GlobalShortcutsDialogProps = {}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(open) : next;
    if (!isControlled) setInternalOpen(value);
    onOpenChange?.(value);
  };
  const pageSections = usePageShortcutsRegistry();
  const sections: Section[] = [...pageSections, ...GLOBAL_SECTIONS];

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      if (e.key === "?") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, openProp]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Press <Kbd>?</Kbd> anytime to open this list.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {sections.map((section) => (
            <div key={section.heading} className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.heading}
              </h3>
              <ul className="space-y-1.5">
                {section.items.map((item) => (
                  <li key={item.label} className="flex items-center justify-between gap-4">
                    <span className="text-sm text-foreground">{item.label}</span>
                    <span className="flex items-center gap-1">
                      {item.keys.map((k, i) => (
                        <Kbd key={i}>{k}</Kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
